import { addHours, isBefore, parseISO } from 'date-fns'
import {
  array,
  boolean,
  enum_,
  forward,
  type InferOutput,
  minLength,
  null_,
  nullable,
  number,
  object,
  partialCheck,
  picklist,
  pipe,
  string
} from 'valibot'

import { VALIDATION_MESSAGES } from '@/constants/common/validation.constants'
import {
  PRICING_MODE,
  ShipmentOrderAdditionalFormValueSchema
} from '@/features/shipment-orders/models'
import { PARTNER_TIER } from '@/libs/api/types'

/**
 * 端数処理 (roundType) の選択肢。基本運賃の請求(income)/払い(expense) で使用。
 * 付帯費用 (shipment-order-additional-form-value) の RoundTypePicklist と同一定義。
 */
const RoundTypePicklist = picklist(['ROUND_UP', 'ROUND_DOWN', 'ROUND_HALF_UP'])

// 追加: 運行実績作成時の配送ルート詳細のスキーマ定義
// ToursRoutesTableTrで利用するが、作成時はsite, entryTime, exitTimeは不要
// また、siteIdは必須とする
const TourCreateDetailFormValueSchema = object({
  uniqueId: string(),
  id: null_(), // 新規作成なので常にnull
  siteId: nullable(number(VALIDATION_MESSAGES.SELECT_SITE)), // 変更: nullを許容し、numberにメッセージを付与
  // orderNo は送信時にindexを使うため、フォームレベルでの検証は不要
  enable: boolean(),
  isAdded: boolean(),
  entryTime: nullable(string()), // IN時間（到着時間）
  exitTime: nullable(string()) // OUT時間（出発時間）
})

// TODO: distanceを追加するか確認
// 車建・運行建実績作成フォームのバリデーションスキーマ
export const TourCreateFormValueSchema = pipe(
  object({
    dailyReportNo: string(), // 日報番号
    partnerId: number(VALIDATION_MESSAGES.REQUIRED_ITEM), // パートナーID（車両フィルタリング用）
    truckId: number(VALIDATION_MESSAGES.REQUIRED_ITEM), // 車両ID（partnerId選択後にフィルタリング）
    customerId: number(VALIDATION_MESSAGES.REQUIRED_ITEM), // 荷主ID
    shipmentOrderNumber: nullable(string()), // 配送依頼No
    shipmentCategoryId: number(VALIDATION_MESSAGES.REQUIRED_ITEM), // 配送種別ID
    // 実運送会社ID（任意。指定時は車両・ドライバーを実運送会社で絞り込み）
    subPartnerId: nullable(number()),
    // 荷姿は 1 実績 = 1 組（区分 × 数）。依頼側 ShipmentRevision.packageCategoryId /
    // ShipmentDetail.packageCount へ写される（#5354、旧 TourPackage の配列は退役）
    packageCategoryId: nullable(
      number(VALIDATION_MESSAGES.SELECT_PACKAGE_CATEGORY)
    ),
    packageCount: nullable(number(VALIDATION_MESSAGES.INPUT_PACKAGE_COUNT)),
    details: pipe(
      array(TourCreateDetailFormValueSchema), // 変更: 作成用の詳細スキーマを使用
      minLength(2, VALIDATION_MESSAGES.ORIGIN_AND_DESTINATION_REQUIRED) // 最低2つ（発地・着地）必要
    ),
    startAt: string(), // 開始日時
    endAt: string(), // 終了日時
    tourEvidenceFiles: array(
      object({
        key: string(),
        name: string()
      })
    ),
    customerChargeBilledPrice: nullable(number()), // 荷主請求金額
    customerContractId: nullable(number()), // 請求契約ID
    // #3865: 払い運賃（手入力 or 自動計算）。保存連携は本実装で対応
    expense: nullable(number()),
    // #3865: 請求側・払い側の税区分（配車板 #3328 VOC_PJ-761 と同じ案件個別税区分）
    incomeTaxCategoryId: nullable(number()),
    expenseTaxCategoryId: nullable(number()),
    // VOC_PJ-844: 請求側・払い側の内税フラグ（配車板と同方式。true=内税、null/false=外税）
    incomeIsTaxInclusive: nullable(boolean()),
    expenseIsTaxInclusive: nullable(boolean()),
    // VOC_PJ-888 #3917: 請求側・払い側の端数処理（切上/切捨/四捨五入、null=カスケード fallback）
    incomeRoundType: nullable(RoundTypePicklist),
    expenseRoundType: nullable(RoundTypePicklist),
    // #3865: 請求側付帯費用
    additionalCharges: array(ShipmentOrderAdditionalFormValueSchema),
    // #3865: 支払側付帯費用
    additionalPayments: array(ShipmentOrderAdditionalFormValueSchema),
    truckModelId: nullable(number()), // 車両モデルID
    totalWeight: nullable(number()), // 追加: 総積載量
    note: string(), // 備考欄
    driverUserId: nullable(number()),
    driverName: nullable(string()),
    partnerTier: nullable(enum_(PARTNER_TIER)),
    incomeRecordingDate: nullable(string()), // 請求計上日
    expenseRecordingDate: nullable(string()) // 払い計上日
  }),
  forward(
    partialCheck(
      [['startAt'], ['endAt']],
      ({ startAt, endAt }) => isBefore(parseISO(startAt), parseISO(endAt)),
      VALIDATION_MESSAGES.AFTER_START_DATE
    ),
    ['endAt']
  ),
  // VOC_PJ-941 #3984 → #4556: 税区分・端数処理は金額の有無に関わらず必須。
  // INCLUDED（運賃込み）の付帯行のみ対象外。内税フラグは対象外。
  forward(
    partialCheck(
      [['expenseTaxCategoryId']],
      ({ expenseTaxCategoryId }) => expenseTaxCategoryId != null,
      VALIDATION_MESSAGES.REQUIRED_ITEM
    ),
    ['expenseTaxCategoryId']
  ),
  forward(
    partialCheck(
      [['expenseRoundType']],
      ({ expenseRoundType }) => expenseRoundType != null,
      VALIDATION_MESSAGES.REQUIRED_ITEM
    ),
    ['expenseRoundType']
  ),
  forward(
    partialCheck(
      [['incomeTaxCategoryId']],
      ({ incomeTaxCategoryId }) => incomeTaxCategoryId != null,
      VALIDATION_MESSAGES.REQUIRED_ITEM
    ),
    ['incomeTaxCategoryId']
  ),
  forward(
    partialCheck(
      [['incomeRoundType']],
      ({ incomeRoundType }) => incomeRoundType != null,
      VALIDATION_MESSAGES.REQUIRED_ITEM
    ),
    ['incomeRoundType']
  ),
  // 付帯 (請求/払い): 金額が入力された AMOUNT 行に税区分・端数処理が揃っているか。
  forward(
    partialCheck(
      [['additionalCharges']],
      ({ additionalCharges }) =>
        additionalCharges.every(
          (row) =>
            row.pricingMode !== PRICING_MODE.AMOUNT ||
            (row.taxCategoryId != null && row.roundType != null)
        ),
      VALIDATION_MESSAGES.REQUIRED_ITEM
    ),
    ['additionalCharges']
  ),
  forward(
    partialCheck(
      [['additionalPayments']],
      ({ additionalPayments }) =>
        additionalPayments.every(
          (row) =>
            row.pricingMode !== PRICING_MODE.AMOUNT ||
            (row.taxCategoryId != null && row.roundType != null)
        ),
      VALIDATION_MESSAGES.REQUIRED_ITEM
    ),
    ['additionalPayments']
  )
)

export type TourCreateFormValue = InferOutput<typeof TourCreateFormValueSchema>

// 変更: 初期値の型アサーションを調整
const createInitialDetails = (): TourCreateFormValue['details'] => [
  {
    uniqueId: crypto.randomUUID(),
    id: null,
    siteId: undefined as unknown as number, // 初期値はundefinedでOK (フォーム側で扱われる)
    enable: true,
    isAdded: true,
    entryTime: null,
    exitTime: null
  },
  {
    uniqueId: crypto.randomUUID(),
    id: null,
    siteId: undefined as unknown as number, // 初期値はundefinedでOK (フォーム側で扱われる)
    enable: true,
    isAdded: true,
    entryTime: null,
    exitTime: null
  }
]

// フォームの初期値を生成する関数
// パートナー選択を追加したことで段階的な選択フローを実現
// startAtを基準にendAtを計算することで、日をまたぐ場合も正しく処理される
export const createInitialTourCreateFormValue = (): TourCreateFormValue => {
  const startAt = new Date()
  const endAt = addHours(startAt, 4) // 開始時刻の4時間後

  return {
    dailyReportNo: '',
    partnerId: undefined as unknown as number, // パートナー選択（車両フィルタリングの起点）
    subPartnerId: null,
    truckId: undefined as unknown as number, // パートナー選択後に有効化
    customerId: undefined as unknown as number,
    shipmentOrderNumber: null,
    shipmentCategoryId: null as unknown as number,
    packageCategoryId: null,
    packageCount: null,
    details: createInitialDetails(),
    startAt: startAt.toISOString(),
    endAt: endAt.toISOString(),
    tourEvidenceFiles: [],
    customerChargeBilledPrice: null,
    customerContractId: null,
    expense: null,
    incomeTaxCategoryId: null,
    expenseTaxCategoryId: null,
    incomeIsTaxInclusive: null,
    expenseIsTaxInclusive: null,
    incomeRoundType: null,
    expenseRoundType: null,
    additionalCharges: [],
    additionalPayments: [],
    truckModelId: null,
    totalWeight: null,
    note: '', // 備考欄の初期値
    driverUserId: null,
    driverName: null,
    partnerTier: null,
    incomeRecordingDate: null,
    expenseRecordingDate: null
  }
}

// 後方互換性のため、定数としてもエクスポート
export const initialTourCreateFormValue: TourCreateFormValue =
  createInitialTourCreateFormValue()
